/**
 * Reassign staff / bay on a CONFIRMED booking (DNKADM-0211 U2) — the shell-owned
 * dialog the calendar drawer's Staff/Resource pencils raise.
 *
 * **One dialog, both dimensions.** On open it reads
 * `GET …/{uuid}/assignment-options` (the picker's single source of truth) and
 * renders a Staff and a Bay chooser from it:
 *  - the option flagged `currentlyAssigned` is **preselected** per dimension;
 *  - an `available:false` option is labelled **"(conflict)"** and stays fully
 *    **selectable** — DNKAPI-0357 dropped the freeness gate, so an eligible pick
 *    always wins and the conflict is a WARNING, never a gate. `capacityRemaining`
 *    is deliberately not turned into any enabled/disabled decision: the two
 *    fields are independent (the booking's own capacity-1 Bay reports
 *    `capacityRemaining: 1`, not 0).
 *  - a dimension whose mode is `none` returns an empty list → its dropdown is
 *    **hidden**.
 *
 * **Partial save.** Only the dimension(s) that actually differ from the current
 * assignment are sent (`{ staffId? , resourceId?, reason? }`), so an unchanged
 * dimension is left alone server-side; with nothing changed Save is disabled, so
 * an empty body can never be attempted. `reason` is optional (audit only) —
 * unlike the reschedule's REQUIRED reason.
 *
 * **After the write.** The `200` IS the updated reservation, so it is handed to
 * the shell (`onDone`) to replace the drawer's copy in place and refresh the
 * window — time / price / credits / status are untouched by a reassignment.
 * Errors are mapped HTTP-**status-first** by `toReassignErrorView` and rendered
 * inline with the dialog kept OPEN, so a recoverable failure never costs the
 * admin their context.
 *
 * **Every admin-override refusal is one dialog (DNKAPI-0542 + DNKAPI-0549, DNKADM-0396).**
 * A `409` the override rules cover — `STAFF_CONFLICT` (the coach is already booked),
 * `RESOURCE_CONFLICT` (the bay has no capacity left), `STAFF_OUTSIDE_HOURS` /
 * `STAFF_OVER_CAP` (the coach is off shift / past a cap) — opens the
 * {@link OverrideConflictDialog} with EVERY reason the server returned, instead of the
 * inline error: "Change it" returns here with the chooser the reasons point at focused
 * (its "(conflict)" annotation IS the `assignment-options` read), "Book anyway" (after the
 * acknowledgement tick) resends the identical body with every acknowledge flag that
 * refusal named and no others, Cancel changes nothing.
 *
 * Note the reversal DNKAPI-0549 brings: a bay double-book used to be the DNKAPI-0357
 * warn-and-save. It is now refused unless acknowledged, like the coach. The inline
 * `assignment-options` warning above the Save button stays — it is the *forecast*; this
 * dialog is the *refusal*.
 *
 * NOT reschedule (`PUT /{uuid}` — time + required reason, consumes a reschedule)
 * and NOT `/details` (the DNKADM-0200 confirmation-queue Edit, unconfirmed only).
 */
import { useEffect, useId, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon, resolveTokens } from '@/theme';
import {
  getAssignmentOptions,
  overrideAcknowledgements,
  parseOverrideRefusal,
  reassignReservation,
  type AssignmentOptions,
  type ConflictRefusal,
  type ReassignReservationInput,
  type Reservation,
} from '@/api/reservations';
import { useResource } from '@/hooks/useResource';
import { currentIdOf, isOffered, optionLabel } from './assignmentOptionsDisplay';
import { toReassignErrorView, type ReassignErrorView } from './reservationsError';
import { OverrideConflictDialog } from './OverrideConflictDialog';
import { refusedPicker } from './refusedPicker';
import { focusPicker } from './focusPicker';

interface ReassignAssignmentDialogProps {
  open: boolean;
  reservation: Reservation;
  onClose: () => void;
  /** The `200`'s re-parsed reservation — the shell re-renders the booking in place. */
  onDone: (reassigned: Reservation) => void;
  /** The calendar's copy is out of date (terminal / no longer confirmed) — refresh. */
  onStale: () => void;
}

export function ReassignAssignmentDialog({
  open,
  reservation,
  onClose,
  onDone,
  onStale,
}: ReassignAssignmentDialogProps) {
  const ids = useId();
  const theme = useTheme();
  const t = resolveTokens(theme);
  const titleId = `${ids}-title`;
  const staffFieldId = `${ids}-staff`;
  const resourceFieldId = `${ids}-resource`;
  const reasonId = `${ids}-reason`;

  const uuid = reservation.reservationUuid;

  // The picker pools. Re-read on a 400 (the pool the admin picked from was stale)
  // and from the inline retry on a failed read.
  const options = useResource<AssignmentOptions | null>(
    () => (uuid ? getAssignmentOptions(uuid) : Promise.resolve(null)),
    [uuid],
  );
  const data = options.data;

  // `null` = untouched, so the effective selection falls back to the loaded current
  // assignment — no effect is needed to seed the preselection once the pools arrive.
  const [staffPick, setStaffPick] = useState<string | null>(null);
  const [resourcePick, setResourcePick] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<ReassignErrorView | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // An admin-override refusal (DNKADM-0396): the server's answer — every reason it named —
  // plus the EXACT body it refused, so "Book anyway" resends that body with the flags that
  // refusal named, not a re-derivation from state.
  const [conflict, setConflict] = useState<{
    view: ConflictRefusal;
    input: ReassignReservationInput;
  } | null>(null);
  // "Change it" → focus the chooser the server's reasons point at, once the dialog is gone.
  const staffFieldRef = useRef<HTMLDivElement>(null);
  const resourceFieldRef = useRef<HTMLDivElement>(null);
  const [focusRequest, setFocusRequest] = useState<{ n: number; field: 'staff' | 'resource' }>({
    n: 0,
    field: 'staff',
  });
  useEffect(() => {
    if (focusRequest.n === 0) return;
    focusPicker(
      focusRequest.field === 'resource' ? resourceFieldRef.current : staffFieldRef.current,
    );
  }, [focusRequest]);

  const staffList = data?.staff ?? [];
  const resourceList = data?.resources ?? [];
  const currentStaff = currentIdOf(data?.staff, reservation.assignedStaffId);
  const currentResource = currentIdOf(data?.resources, reservation.assignedResourceId);
  // A pick is honored only while the LOADED pool still offers it. That matters after a
  // `400 …_NOT_ASSIGNABLE` re-fetch, whose whole point is that the picked id was not
  // assignable: if the refreshed pool drops it, an untouched `useState` would leave the
  // chooser showing a value with no matching option AND keep Save enabled to re-send the
  // id that just failed. Falling back to the freshly-loaded current assignment self-heals
  // both. Unchanged pools are unaffected.
  const pickedStaff =
    staffPick !== null && staffList.some((o) => o.id === staffPick) ? staffPick : currentStaff;
  const pickedResource =
    resourcePick !== null && resourceList.some((o) => o.id === resourcePick)
      ? resourcePick
      : currentResource;

  const showStaff = data !== null && isOffered(data.staffMode, staffList);
  const showResource = data !== null && isOffered(data.resourceMode, resourceList);
  const nothingToPick = options.status === 'ready' && !showStaff && !showResource;

  const staffChanged = showStaff && pickedStaff !== '' && pickedStaff !== currentStaff;
  const resourceChanged =
    showResource && pickedResource !== '' && pickedResource !== currentResource;

  // Double-book warnings — only for a CHANGED pick, since re-selecting the current
  // assignment is a no-op the save can't even send.
  const conflicts: { key: string; name: string }[] = [];
  const staffConflict = staffChanged ? staffList.find((o) => o.id === pickedStaff) : undefined;
  if (staffConflict && !staffConflict.available) {
    conflicts.push({ key: 'staff', name: staffConflict.name.trim() || 'that staff member' });
  }
  const resourceConflict = resourceChanged
    ? resourceList.find((o) => o.id === pickedResource)
    : undefined;
  if (resourceConflict && !resourceConflict.available) {
    conflicts.push({ key: 'resource', name: resourceConflict.name.trim() || 'that bay' });
  }

  const blocked = error?.blocked ?? false;
  const canSave = (staffChanged || resourceChanged) && !submitting && !blocked;

  function close() {
    if (submitting) return;
    onClose();
  }

  async function handleSave() {
    setError(null);
    if (!uuid) {
      setError({
        message: 'This reservation can’t be reassigned (missing id).',
        blocked: true,
        refetchOptions: false,
        stale: false,
      });
      return;
    }
    // Partial body: only the dimensions that actually differ (+ an optional reason).
    // Save is disabled when neither differs, so this is never empty.
    const input: ReassignReservationInput = {};
    if (staffChanged) input.staffId = pickedStaff;
    if (resourceChanged) input.resourceId = pickedResource;
    const trimmedReason = reason.trim();
    if (trimmedReason.length > 0) input.reason = trimmedReason;
    await send(uuid, input);
  }

  /** The one write path — the first attempt and the acknowledged resend share it. */
  async function send(uuid: string, input: ReassignReservationInput) {
    setError(null);
    setSubmitting(true);
    try {
      const updated = await reassignReservation(uuid, input);
      onDone(updated);
    } catch (err) {
      setSubmitting(false);
      const refusal = parseOverrideRefusal(err);
      if (refusal) {
        setConflict({ view: refusal, input });
        return;
      }
      const view = toReassignErrorView(err);
      setError(view);
      if (view.refetchOptions) options.refetch();
      if (view.stale) onStale();
    }
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth aria-labelledby={titleId}>
      <DialogTitle id={titleId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}>
        <Avatar variant="rounded" sx={{ bgcolor: t.brandSoft, color: t.brand, width: 38, height: 38 }}>
          <Icon name="swap_horiz" sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Change staff or bay</Typography>
          <Typography variant="body2">{reservation.serviceName ?? 'Reservation'}</Typography>
        </Box>
        <IconAction size="small" onClick={close} label="Close">
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>

      <DialogContent>
        {/* Static explanatory copy, not an announcement — `role="note"` keeps MUI's
            default `role="alert"` off it so the live regions below stay unambiguous. */}
        <Alert severity="info" role="note" icon={<Icon name="schedule" />} sx={{ mb: 2 }}>
          The time, price, and credits stay exactly as they are — only the assignment
          changes.
        </Alert>

        {options.status === 'loading' && (
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, py: 1.5 }} role="status">
            <CircularProgress size={18} />
            <Typography sx={{ color: t.text2 }}>Loading options…</Typography>
          </Stack>
        )}

        {options.status === 'error' && (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button color="inherit" size="small" onClick={options.refetch}>
                Retry
              </Button>
            }
          >
            The assignment options couldn’t be loaded. Try again.
          </Alert>
        )}

        {showStaff && (
          <TextField
            id={staffFieldId}
            ref={staffFieldRef}
            select
            label="Staff"
            fullWidth
            size="small"
            value={pickedStaff}
            onChange={(e) => setStaffPick(e.target.value)}
            disabled={submitting || blocked}
            sx={{ mb: 2 }}
          >
            {staffList.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {optionLabel(o)}
              </MenuItem>
            ))}
          </TextField>
        )}

        {showResource && (
          <TextField
            id={resourceFieldId}
            ref={resourceFieldRef}
            select
            label="Bay"
            fullWidth
            size="small"
            value={pickedResource}
            onChange={(e) => setResourcePick(e.target.value)}
            disabled={submitting || blocked}
            sx={{ mb: 2 }}
          >
            {resourceList.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {optionLabel(o)}
              </MenuItem>
            ))}
          </TextField>
        )}

        {nothingToPick && (
          <Alert severity="info" role="note">
            This service assigns its staff and bay automatically, so there is nothing to
            change here.
          </Alert>
        )}

        {/* An admin override is allowed — this warns, it does NOT block the save. */}
        {conflicts.map((c) => (
          <Alert key={c.key} severity="warning" sx={{ mb: 2 }}>
            This will double-book {c.name} at this time.
          </Alert>
        ))}

        {(showStaff || showResource) && (
          <TextField
            id={reasonId}
            label="Reason (optional)"
            fullWidth
            size="small"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting || blocked}
            placeholder="e.g. Coach called out"
            helperText="Optional — logged to the audit trail so the team has context."
          />
        )}

        {error && (
          <Alert severity="error" role="alert" sx={{ mt: 2 }}>
            {error.message}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={close} disabled={submitting} sx={{ color: t.text2 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<Icon name="check" />}
          onClick={() => void handleSave()}
          disabled={!canSave}
        >
          {submitting ? 'Saving…' : 'Save assignment'}
        </Button>
      </DialogActions>

      {/* The admin-override refusal (DNKADM-0396) — a dialog over this one, never a toast.
          Mounted only while a refusal is pending so its acknowledgement tick starts clean
          every time. */}
      {conflict && uuid && (
        <OverrideConflictDialog
          open
          refusal={conflict.view}
          submitting={submitting}
          onCancel={() => setConflict(null)}
          onChangeIt={() => {
            const field = refusedPicker(conflict.view);
            setConflict(null);
            // DNKADM-0437 V-24: the pools were read when the dialog opened; the refusal says
            // availability moved since, so re-read before the operator picks again.
            options.refetch();
            setFocusRequest((prev) => ({ n: prev.n + 1, field }));
          }}
          onBookAnyway={() => {
            const { input, view } = conflict;
            setConflict(null);
            // Exactly the flags THIS refusal named — never one for a decision the admin
            // was not shown.
            void send(uuid, { ...input, ...overrideAcknowledgements(view) });
          }}
        />
      )}
    </Dialog>
  );
}
